import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Box } from '@mui/material'
import {
  ChartKey,
  ITrendChartsFilters,
  TDateRange,
} from 'types/models/DataAnalysis'
import {
  patchDraftFilters,
  setDraftFilters,
  setWheatColors,
  patchGlobalDraftFilters,
  setGlobalDraftFilters,
} from 'store/slices/dataAnalysisSlice'
import {
  selectAppliedFilters,
  selectDraftFilters,
  selectIsApplyGlobally,
  selectGlobalDraftFilters,
  selectGlobalAppliedFilters,
} from 'store/selectors/dataAnalysisSelectors'
import { useFilterData } from './useFiltersData'
import { DATA_ANALYSIS_FILTERS } from '../../utils/consts'
import { getDefaultDateRange } from '../../utils/consts'
import { DropdownOption } from '../../../../components/ui/Dropdown/Dropdown'
import {
  BinFilter,
  DateFilter,
  ProductsFilter,
  SingleProductFilter,
  WheatCodeFilter,
} from './filters'

export enum FILTERS_MODE {
  LIST = 'list',
  AUTOCOMPLETE = 'autocomplete',
}

interface IDataAnalysisFiltersProps {
  filtersList: DATA_ANALYSIS_FILTERS[]
  chartKey: ChartKey
  hydrateFrom?: 'draft' | 'applied'
}

const DataAnalysisFilters = ({
  filtersList,
  chartKey,
  hydrateFrom = 'draft',
}: IDataAnalysisFiltersProps) => {
  const dispatch = useDispatch()
  const initializedRef = useRef(false)
  const isApplyGlobally = useSelector(selectIsApplyGlobally)
  const globalDraft = useSelector(selectGlobalDraftFilters)

  const sourceSelector = useMemo(() => {
    if (isApplyGlobally) {
      return hydrateFrom === 'applied'
        ? selectGlobalAppliedFilters
        : selectGlobalDraftFilters
    }

    return hydrateFrom === 'applied'
      ? selectAppliedFilters(chartKey)
      : selectDraftFilters(chartKey)
  }, [hydrateFrom, chartKey, isApplyGlobally])

  const source = useSelector(sourceSelector)

  const { bins, wheatCodes, products, wheatCodeColorMap } =
    useFilterData(filtersList)

  const defaultDateRange: TDateRange = useMemo(() => getDefaultDateRange(), [])

  useEffect(() => {
    if (!initializedRef.current && !source) {
      const initial: ITrendChartsFilters = {
        wheat_codes: [],
        bins: [],
        blend_types: [],
        blend_type: undefined,
        date_range: defaultDateRange,
      }

      // Only initialize chart-specific filters if not in global mode
      if (!isApplyGlobally) {
        dispatch(setDraftFilters({ key: chartKey as any, filters: initial }))
      }

      // Initialize global filters if in global mode and they don't exist
      if (isApplyGlobally && !globalDraft) {
        dispatch(setGlobalDraftFilters(initial))
      }

      initializedRef.current = true
    }
  }, [
    source,
    chartKey,
    dispatch,
    isApplyGlobally,
    globalDraft,
    defaultDateRange,
  ])

  useEffect(() => {
    if (!wheatCodes.length || Object.keys(wheatCodeColorMap).length === 0)
      return

    const items = wheatCodes.map((w: any) => ({
      id: Number(w.id),
      code: String(w.code),
    }))

    const byId = wheatCodeColorMap
    const byCode = Object.fromEntries(items.map((w) => [w.code, byId[w.id]]))

    dispatch(setWheatColors({ byId, byCode }))
  }, [wheatCodes, wheatCodeColorMap, dispatch])

  const selectedWc = source?.wheat_codes ?? []
  const selectedBins = source?.bins ?? []
  const selectedBlends = source?.blend_types ?? []
  const dateRange = source?.date_range ?? defaultDateRange

  const handleDateChange = useCallback(
    (next: TDateRange) => {
      if (isApplyGlobally) {
        dispatch(patchGlobalDraftFilters({ date_range: next }))
      } else {
        dispatch(
          patchDraftFilters({ key: chartKey, patch: { date_range: next } })
        )
      }
    },
    [dispatch, chartKey, isApplyGlobally]
  )

  const handleWcListChange = useCallback(
    (nextSelected: string[]) => {
      if (isApplyGlobally) {
        dispatch(patchGlobalDraftFilters({ wheat_codes: nextSelected }))
      } else {
        dispatch(
          patchDraftFilters({
            key: chartKey,
            patch: { wheat_codes: nextSelected },
          })
        )
      }
    },
    [dispatch, chartKey, isApplyGlobally]
  )

  const handleBinListChange = useCallback(
    (nextSelected: string[]) => {
      if (isApplyGlobally) {
        dispatch(patchGlobalDraftFilters({ bins: nextSelected }))
      } else {
        dispatch(
          patchDraftFilters({ key: chartKey, patch: { bins: nextSelected } })
        )
      }
    },
    [dispatch, chartKey, isApplyGlobally]
  )

  const handleBlendsChange = useCallback(
    (nextSelected: string[]) => {
      if (isApplyGlobally) {
        dispatch(patchGlobalDraftFilters({ blend_types: nextSelected }))
      } else {
        dispatch(
          patchDraftFilters({
            key: chartKey,
            patch: { blend_types: nextSelected },
          })
        )
      }
    },
    [dispatch, chartKey, isApplyGlobally]
  )

  const handleFlourProductSelect = useCallback(
    (nextSelected: DropdownOption) => {
      if (isApplyGlobally) {
        dispatch(patchGlobalDraftFilters({ blend_type: nextSelected }))
      } else {
        dispatch(
          patchDraftFilters({
            key: chartKey,
            patch: { blend_type: nextSelected },
          })
        )
      }
    },
    [dispatch, chartKey, isApplyGlobally]
  )

  return (
    <Box>
      {filtersList.map((filter, index) => {
        switch (filter) {
          case DATA_ANALYSIS_FILTERS.DATE:
            return (
              <Box key={`date-${index}`} sx={{ mb: 2 }}>
                <DateFilter
                  dateRange={dateRange}
                  onDateChange={handleDateChange}
                />
              </Box>
            )

          case DATA_ANALYSIS_FILTERS.WHEAT_CODE:
            return (
              <Box key={`wheatCode-${index}`} sx={{ mb: 1 }}>
                <WheatCodeFilter
                  wheatCodes={wheatCodes}
                  wheatCodeColorMap={wheatCodeColorMap}
                  selectedCodes={selectedWc}
                  onCodesChange={handleWcListChange}
                />
              </Box>
            )

          case DATA_ANALYSIS_FILTERS.BIN:
            return (
              <Box key={`bin-${index}`} sx={{ mb: 2 }}>
                <BinFilter
                  bins={bins}
                  selectedBins={selectedBins}
                  onBinsChange={handleBinListChange}
                />
              </Box>
            )

          case DATA_ANALYSIS_FILTERS.PRODUCTS:
            return (
              <Box key={`products-${index}`} sx={{ mb: 2 }}>
                <ProductsFilter
                  products={products}
                  selectedProducts={selectedBlends}
                  onProductsChange={handleBlendsChange}
                />
              </Box>
            )

          case DATA_ANALYSIS_FILTERS.FLOUR_PRODUCT:
            return (
              <Box key={`flour-product-${index}`} sx={{ mb: 2 }}>
                <SingleProductFilter
                  products={products}
                  selectedProduct={source?.blend_type}
                  onProductSelect={handleFlourProductSelect}
                />
              </Box>
            )

          default:
            return null
        }
      })}
    </Box>
  )
}

export default DataAnalysisFilters
